import { differenceInCalendarDays, startOfDay } from 'date-fns';

import { ListingSubtype, TransactionMode } from 'app/types/listing';
import { isDonationListing } from 'app/lib/listing';
import type { Loan } from 'app/types/loan';
import { LoanStatus, LoanType } from 'app/types/loan';

export const UPCOMING_ACTIVITY_WINDOW_DAYS = 3;

export type UpcomingActivityKind =
  | 'completePurchase'
  | 'completeSale'
  | 'confirmReturn'
  | 'confirmSale'
  | 'expectReturn'
  | 'handOff'
  | 'pickUp'
  | 'returnItem';

export type UpcomingActivityPriority = 'attention' | 'overdue' | 'soon' | 'today';

export interface UpcomingActivityEvent {
  date: Date;
  daysFromToday: number;
  kind: UpcomingActivityKind;
  loan: Loan;
  loanType: LoanType;
  priority: UpcomingActivityPriority;
}

function getPriority(daysFromToday: number, kind: UpcomingActivityKind): UpcomingActivityPriority {
  if (kind === 'confirmReturn' || kind === 'confirmSale') return 'attention';
  if (daysFromToday < 0) return 'overdue';
  if (daysFromToday === 0) return 'today';
  return 'soon';
}

function createEvent(
  loan: Loan,
  loanType: LoanType,
  kind: UpcomingActivityKind,
  date: Date,
  today: Date,
): UpcomingActivityEvent {
  const daysFromToday = differenceInCalendarDays(startOfDay(date), today);

  return {
    date,
    daysFromToday,
    kind,
    loan,
    loanType,
    priority: getPriority(daysFromToday, kind),
  };
}

function getNextLoanEvent(loan: Loan, loanType: LoanType, today: Date): UpcomingActivityEvent | null {
  const start = new Date(loan.start);
  const end = new Date(loan.end);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
  const isDonation = isDonationListing(loan.listing);
  const isSell = loan.listing.transactionMode === TransactionMode.sell;

  if (loan.status === LoanStatus.Returned) {
    if (loanType !== LoanType.Lending) return null;
    if (isSell) {
      const returned = new Date(loan.returned ?? loan.end);
      if (Number.isNaN(returned.getTime())) return null;
      return createEvent(loan, loanType, 'confirmSale', returned, today);
    }
    if (isDonation) return null;
    if (loan.listing.subtype !== ListingSubtype.object) return null;
    const returned = new Date(loan.returned ?? loan.end);
    if (Number.isNaN(returned.getTime())) return null;
    return createEvent(loan, loanType, 'confirmReturn', returned, today);
  }

  if (loan.status !== LoanStatus.Accepted) return null;

  const startDay = startOfDay(start);
  const endDay = startOfDay(end);
  const startsAfterToday = startDay > today;
  const startsToday = startDay.getTime() === today.getTime() && endDay >= today;

  if (startsAfterToday || startsToday) {
    return createEvent(loan, loanType, loanType === LoanType.Lending ? 'handOff' : 'pickUp', start, today);
  }

  if (isDonation) return null;
  if (isSell) {
    return createEvent(loan, loanType, loanType === LoanType.Lending ? 'completeSale' : 'completePurchase', end, today);
  }
  if (loan.listing.subtype !== ListingSubtype.object) return null;

  return createEvent(loan, loanType, loanType === LoanType.Lending ? 'expectReturn' : 'returnItem', end, today);
}

const PRIORITY_ORDER: Record<UpcomingActivityPriority, number> = {
  overdue: 0,
  attention: 1,
  today: 2,
  soon: 3,
};

const ACTION_ORDER: Record<UpcomingActivityKind, number> = {
  confirmSale: 0,
  confirmReturn: 0,
  completePurchase: 1,
  completeSale: 1,
  returnItem: 1,
  handOff: 2,
  pickUp: 3,
  expectReturn: 4,
};

export function buildUpcomingActivityEvents({
  borrowings,
  lendings,
  now = new Date(),
  windowDays = UPCOMING_ACTIVITY_WINDOW_DAYS,
}: {
  borrowings: Loan[];
  lendings: Loan[];
  now?: Date;
  windowDays?: number | null;
}): UpcomingActivityEvent[] {
  const today = startOfDay(now);
  const seenLoanIds = new Set<Loan['id']>();

  return [
    ...lendings.map((loan) => ({ loan, loanType: LoanType.Lending })),
    ...borrowings.map((loan) => ({ loan, loanType: LoanType.Borrowing })),
  ]
    .map(({ loan, loanType }) => getNextLoanEvent(loan, loanType, today))
    .filter((event): event is UpcomingActivityEvent => {
      if (!event || seenLoanIds.has(event.loan.id)) return false;
      if (windowDays !== null && (event.daysFromToday < -windowDays || event.daysFromToday > windowDays)) {
        return false;
      }
      seenLoanIds.add(event.loan.id);
      return true;
    })
    .sort((left, right) => {
      const priorityDifference = PRIORITY_ORDER[left.priority] - PRIORITY_ORDER[right.priority];
      if (priorityDifference !== 0) return priorityDifference;
      const dateDifference = left.date.getTime() - right.date.getTime();
      if (dateDifference !== 0) return dateDifference;
      return ACTION_ORDER[left.kind] - ACTION_ORDER[right.kind];
    });
}
